import { BaseExportModal, ExportModalProps } from './ExportModal'
import { createExportColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { DEAL_COLUMNS } from 'components/DataExpansionComponents/Deals/dealsColumnInfo'
import { useMemo } from 'react'
import { featureFlagService } from 'utils/featureFlagService'
import { useDownload } from 'features/download-widget'
import {
  getDownloadStatus,
  startDealsReportExportV2,
  startDealsExportV2,
} from 'services/downloadsApi'

import { useColumnStoreContext } from 'stores/ColumnsStore/useColumnsStoreContext'

interface DealExportModalProps extends ExportModalProps {
  kind: 'deals_table' | 'deals_report'
}

export default function DealExportModal(props: DealExportModalProps) {
  const { enableEmailIntegration } = featureFlagService()
  const dealColumnHelper = createExportColumnHelper({
    columns: DEAL_COLUMNS,
  })
  const selectedColumns = useColumnStoreContext(
    (state) => state.selectedColumns
  )
  const { startDownload } = useDownload()

  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Company Attributes',
        elements: [
          dealColumnHelper.retrieveColumn('company'),
          dealColumnHelper.retrieveColumn('category'),
          dealColumnHelper.retrieveColumn('sub_category'),
          dealColumnHelper.retrieveColumn('company_size'),
          dealColumnHelper.retrieveColumn('taglist'),
          dealColumnHelper.retrieveColumn('contact_tags'),
          dealColumnHelper.retrieveColumn('distributors'),
          dealColumnHelper.retrieveColumn('gpos'),
          dealColumnHelper.retrieveColumn('note_count'),
          dealColumnHelper.retrieveColumn('contact_count'),
          dealColumnHelper.retrieveColumn('days_since_last_modified'),
          dealColumnHelper.retrieveColumn('days_since_deal_last_modified'),
          dealColumnHelper.retrieveColumn('estimated_meals_per_day'),
          dealColumnHelper.retrieveColumn('operational_schools'),
          dealColumnHelper.retrieveColumn('doors_count'),
        ],
      },
      {
        title: 'Campaigns and Deals',
        elements: [
          dealColumnHelper.retrieveColumn('deal_name'),
          dealColumnHelper.retrieveColumn('product'),
          dealColumnHelper.retrieveColumn('product_variant'),
          dealColumnHelper.retrieveColumn('sku'),
          dealColumnHelper.retrieveColumn('account_owner'),
          dealColumnHelper.retrieveColumn('sales_stage'),
          dealColumnHelper.retrieveColumn('last_datetime_stage_changed'),
          dealColumnHelper.retrieveColumn('expected_days_to_close'),
          dealColumnHelper.retrieveColumn('close_date_override'),
          dealColumnHelper.retrieveColumn('origin_campaign'),
          dealColumnHelper.retrieveColumn('days_since_reply'),
          dealColumnHelper.retrieveColumn('days_since_sent'),
          dealColumnHelper.retrieveColumn('modified'),
          dealColumnHelper.retrieveColumn('created'),
        ],
      },
      {
        title: 'Forecast',
        elements: [
          dealColumnHelper.retrieveColumn('monthly_volume_predicted'),
          dealColumnHelper.retrieveColumn('monthly_volume_override'),
          dealColumnHelper.retrieveColumn('monthly_revenue_predicted'),
          dealColumnHelper.retrieveColumn('monthly_revenue_override'),
          dealColumnHelper.retrieveColumn('annual_volume'),
          dealColumnHelper.retrieveColumn('annual_revenue'),
          dealColumnHelper.retrieveColumn('annual_brand_opp'),
          dealColumnHelper.retrieveColumn('annual_total'),
        ],
      },
    ]
    if (!enableEmailIntegration) {
      cols[1].elements = cols[1].elements.filter(
        (col) => col[0] !== 'days_since_reply' && col[0] !== 'days_since_sent'
      )
    }

    return cols
  }, [enableEmailIntegration])

  const exportApi =
    props.kind === 'deals_report'
      ? startDealsReportExportV2
      : startDealsExportV2

  const onSubmit = async (
    selectedCols: string[],
    exportType: 'csv' | 'xlsx'
  ) => {
    await startDownload({
      items: [{ id: 0, name: 'Exporting Deals...' }],
      initiate: async () => {
        const { sort, ...filterWithoutSort } = (props.filters ?? {}) as Record<
          string,
          unknown
        >
        const response = await exportApi({
          selected_columns: selectedCols,
          export_type: exportType,
          filter_params: {
            ...filterWithoutSort,
            product: props.productSelected,
            include_ids: props.selectedIds ?? [],
            exclude_ids: props.excludeIds ?? [],
          },
          sort: sort as string | undefined,
        })
        return { type: 'async', requestId: response.request_id }
      },
      getStatus: getDownloadStatus,
      label: 'deals',
    })
  }

  return (
    <BaseExportModal
      title="Select Columns for Deals Download"
      subTitle="Download up to 10,000 deals at a time, with any of the following columns. Any filters already applied will remain active."
      submitText="Export Deals"
      columns={columns}
      alwaysSelectedColumns={['sku']}
      selectedColumns={selectedColumns}
      onSubmit={onSubmit}
      show={props.show}
      handleClose={props._handleClose}
    />
  )
}
